import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useAuthStore } from '../../store/authStore';

// Apple App Store Guideline 5.1.1(v): apps with account creation must offer in-app account deletion.
export default function DeleteAccountDialog({ variant = 'icon' }) {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  const { logout } = useAuthStore();

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete('/auth/delete-account');
      toast.success('Your account has been permanently deleted.');
      setOpen(false);
      logout();
      navigate('/');
    } catch {
      toast.error('Could not delete account. Please try again or email support@diamondscorer.com');
      setDeleting(false);
    }
  };

  return (
    <>
      {variant === 'icon' ? (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => { setConfirmText(''); setOpen(true); }}
          className="text-stone-400 hover:text-red-600"
          title="Delete Account"
          data-testid="delete-account-btn"
        >
          <Trash2 size={16} />
        </Button>
      ) : (
        <button
          type="button"
          onClick={() => { setConfirmText(''); setOpen(true); }}
          className="text-xs text-stone-400 hover:text-red-600 underline underline-offset-2"
          data-testid="delete-account-btn"
        >
          Delete Account
        </button>
      )}

      <Dialog open={open} onOpenChange={(v) => { if (!deleting) setOpen(v); }}>
        <DialogContent className="max-w-md" data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle size={18} /> Delete Your Account?
            </DialogTitle>
            <DialogDescription>This action is permanent and cannot be undone.</DialogDescription>
          </DialogHeader>

          <div className="space-y-3 text-sm text-stone-600">
            <p className="font-semibold text-stone-800">Deleting your account will permanently remove:</p>
            <ul className="list-disc pl-5 space-y-1 text-xs">
              <li>Your login and profile information</li>
              <li>Teams you created, including all rosters, games, stats, and play-by-play history</li>
              <li>Your messages, schedules, tournaments, and followed teams</li>
              <li>Any active subscription access tied to this account</li>
            </ul>
            <p className="text-xs text-stone-500">
              Teams created by other coaches are not affected — you'll simply be removed from them.
            </p>
            <div className="pt-1">
              <p className="text-xs font-semibold text-stone-700 mb-1.5">Type <span className="font-mono bg-stone-100 px-1 rounded">DELETE</span> to confirm:</p>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE"
                autoComplete="off"
                data-testid="delete-account-confirm-input"
              />
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" disabled={deleting} onClick={() => setOpen(false)} data-testid="delete-account-cancel-btn">
              Cancel
            </Button>
            <Button
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              disabled={confirmText.trim().toUpperCase() !== 'DELETE' || deleting}
              onClick={handleDelete}
              data-testid="delete-account-confirm-btn"
            >
              {deleting ? <Loader2 size={16} className="animate-spin" /> : 'Permanently Delete'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
